import type { ShotChallenge } from '@/api/capture';
import { useT } from '@/i18n/runtime';
import { challengeText } from '@/i18n/serverText';
import { Icon } from './Icon';

/**
 * The in-frame challenge card (FSD §4.4): the server-authored challenge text verbatim, typed by
 * kind (show / speak / act), plus the short visit code the reviewer cross-checks. Rendered ABOVE
 * the viewfinder while a challenge shot records, and as a primer before it starts.
 *
 * `texts` is the challenge ROW's `promptTexts`: the be issues `challenge.text === promptText` with the bank entry's
 * `{en, hi}` beside it, so the sentence is resolved in the chosen language exactly like any step (FSD-18 D2). When
 * they don't match, `challengeText` keeps the challenge's own words; it never borrows the step's.
 *
 * `shownAbove` is the step prompt the instruction card already shows (resolved in the same language). When the
 * sentence is that same text the card does not print it twice, so the code sits higher in the frame (FSD-20 D8).
 */
export function ChallengeOverlay({
  challenge,
  texts,
  nonce,
  live,
  shownAbove,
}: {
  challenge: ShotChallenge;
  texts?: Readonly<Record<string, string>> | undefined;
  nonce: string;
  live: boolean;
  shownAbove?: string;
}): JSX.Element {
  const { t, locale } = useT();
  const sentence = challengeText(challenge, texts, locale);
  const repeats = shownAbove !== undefined && shownAbove.trim() === sentence.text.trim();
  const icon = challenge.type === 'SPEAK_NONCE' ? 'mic' : challenge.type === 'SHOW_NONCE' ? 'doc' : 'cam';
  return (
    <div className={`sv-challenge${live ? ' is-live' : ''}`} role="note" data-testid="challenge-overlay" data-kind={challenge.type}>
      <div className="sv-challenge-head">
        <Icon name={icon} size={16} />
        <span>{t(`sv.capture.challenge.${challenge.type}`)}</span>
      </div>
      {repeats ? null : <div className="sv-challenge-text" lang={sentence.locale} dir="auto">{sentence.text}</div>}
      {challenge.type !== 'ACTION' ? (
        <div className="sv-challenge-code mono" data-testid="challenge-code" aria-label={t('sv.capture.visitCode', { code: nonce.slice(0, 8).toUpperCase() })}>
          {nonce.slice(0, 8).toUpperCase().split('').join(' ')}
        </div>
      ) : null}
      {/* the visit code always shows; the "why" sentence gives way on a short screen (FSD-20 D12) */}
      <div className="sv-challenge-meta mono">
        <span>{t('sv.capture.visitCode', { code: nonce.slice(0, 8).toUpperCase() })}</span>
        <span className="sv-challenge-why"> · {t('sv.capture.challenge.why')}</span>
      </div>
    </div>
  );
}
